import { Routes } from '@angular/router';
import { internalUserGuard } from './core/guards/internal-user.guard';

/**
 * `data.robots: 'noindex'` keeps a route out of search indexes — `SeoService` writes the meta
 * tag from it on every navigation. It marks the per-visitor flows (an offer, the editor, the
 * quote form), the staff pages and the 404, none of which is a page for anyone but the person in
 * it. Everything without the flag is `index, follow`.
 */
export const routes: Routes = [
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () =>
      import('./features/search/pages/search-page/search-page.component').then(
        (m) => m.SearchPageComponent,
      ),
  },
  {
    path: 'editor/:id',
    data: { campaignBanner: 'quiet', robots: 'noindex' },
    loadComponent: () =>
      import('./features/editor/pages/editor-page/editor-page.component').then(
        (m) => m.EditorPageComponent,
      ),
  },
  {
    path: 'cruises/:id',
    data: { robots: 'noindex' },
    loadComponent: () =>
      import('./features/search/pages/cruise-start-page/cruise-start-page.component').then(
        (m) => m.CruiseStartPageComponent,
      ),
  },
  {
    path: 'offer/new',
    data: { campaignBanner: 'quiet', robots: 'noindex' },
    loadComponent: () =>
      import('./features/offer/pages/new-offer-page/new-offer-page.component').then(
        (m) => m.NewOfferPageComponent,
      ),
  },
  {
    path: 'offer/:id',
    data: { campaignBanner: 'quiet', robots: 'noindex' },
    loadComponent: () =>
      import('./features/offer/pages/offer-page/offer-page.component').then(
        (m) => m.OfferPageComponent,
      ),
  },
  {
    path: 'sisaiset',
    data: { campaignBanner: 'off', robots: 'noindex' },
    canActivate: [internalUserGuard],
    loadComponent: () =>
      import('./features/internal/pages/internal-hub-page/internal-hub-page.component').then(
        (m) => m.InternalHubPageComponent,
      ),
  },
  {
    path: 'internal/custom-offer',
    data: { campaignBanner: 'off', robots: 'noindex' },
    canActivate: [internalUserGuard],
    loadComponent: () =>
      import('./features/internal/pages/custom-offer-page/custom-offer-page.component').then(
        (m) => m.CustomOfferPageComponent,
      ),
  },
  {
    path: 'internal/analytics',
    data: { campaignBanner: 'off', robots: 'noindex' },
    canActivate: [internalUserGuard],
    loadComponent: () =>
      import('./features/internal/pages/internal-analytics-page/internal-analytics-page.component').then(
        (m) => m.InternalAnalyticsPageComponent,
      ),
  },
  {
    path: 'internal/offers',
    data: { campaignBanner: 'off', robots: 'noindex' },
    canActivate: [internalUserGuard],
    loadComponent: () =>
      import('./features/internal/pages/internal-offers-page/internal-offers-page.component').then(
        (m) => m.InternalOffersPageComponent,
      ),
  },
  {
    path: 'sivut/:slug',
    loadComponent: () =>
      import('./features/pages/wp-content-page/wp-content-page.component').then(
        (m) => m.WpContentPageComponent,
      ),
  },
  {
    path: 'ota-yhteytta',
    loadComponent: () =>
      import('./features/contact/contact-page.component').then((m) => m.ContactPageComponent),
  },
  {
    path: 'kohteet',
    data: { kind: 'ryhmamatkat' },
    loadComponent: () =>
      import('./features/content/content-category-page/content-category-page.component').then(
        (m) => m.ContentCategoryPageComponent,
      ),
  },
  {
    path: 'kohteet/:slug',
    data: { kind: 'ryhmamatkat' },
    loadComponent: () =>
      import('./features/content/content-article-page/content-article-page.component').then(
        (m) => m.ContentArticlePageComponent,
      ),
  },
  {
    // The commercial landing page, not the generic category listing: `/ryhmille` is where both
    // organic search and the ads arrive, and it has an order of its own.
    path: 'ryhmille',
    loadComponent: () =>
      import('./features/content/groups-landing-page/groups-landing-page.component').then(
        (m) => m.GroupsLandingPageComponent,
      ),
  },
  {
    path: 'ryhmille/:slug',
    data: { kind: 'ryhmille' },
    loadComponent: () =>
      import('./features/content/content-article-page/content-article-page.component').then(
        (m) => m.ContentArticlePageComponent,
      ),
  },
  {
    path: 'matkaideat',
    data: { kind: 'matkaideat' },
    loadComponent: () =>
      import('./features/content/content-category-page/content-category-page.component').then(
        (m) => m.ContentCategoryPageComponent,
      ),
  },
  {
    path: 'matkaideat/:slug',
    data: { kind: 'matkaideat' },
    loadComponent: () =>
      import('./features/content/content-article-page/content-article-page.component').then(
        (m) => m.ContentArticlePageComponent,
      ),
  },
  { path: 'ryhmamatkat', redirectTo: 'kohteet', pathMatch: 'full' },
  { path: 'ryhmamatkat/:slug', redirectTo: 'kohteet/:slug' },
  {
    path: 'tarjouspyynto',
    data: { campaignBanner: 'quiet', robots: 'noindex' },
    loadComponent: () =>
      import('./features/search/pages/quote-request-page/quote-request-page.component').then(
        (m) => m.QuoteRequestPageComponent,
      ),
  },
  {
    path: 'pyyda-tarjous-ryhmalle',
    loadComponent: () =>
      import('./features/search/pages/quote-landing-page/quote-landing-page.component').then(
        (m) => m.QuoteLandingPageComponent,
      ),
  },
  {
    path: '404',
    data: { robots: 'noindex', notFound: true },
    loadComponent: () =>
      import('./features/pages/not-found-page/not-found-page.component').then(
        (m) => m.NotFoundPageComponent,
      ),
  },
  {
    // Rendered at the address the visitor typed, not redirected to `/`: a redirect turned every
    // typo into a second copy of the front page, and a page that says "not found" is what both
    // a person and a crawler need to see there.
    //
    // `notFound` is what tells `SeoService` this page is the not-found page wherever it renders:
    // its title and description come from the `/404` entry rather than from the typed address,
    // and it writes no canonical. See the note on `data.robots` above.
    path: '**',
    data: { robots: 'noindex', notFound: true },
    loadComponent: () =>
      import('./features/pages/not-found-page/not-found-page.component').then(
        (m) => m.NotFoundPageComponent,
      ),
  },
];
